import React, { useContext, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { TabsContext, TabsContextResponse } from "./tabs-helper";

interface TabsProps {
  /**
   * Vertical orientation
   */
  vertical?: boolean;
  /**
   * Extend classname
   */
  extendClasses?: string;
  /**
   * Content
   */
  children?: React.ReactNode;
  /**
   * handle change active
   */
  value?: string;
}

const Root: React.FC<TabsProps> = ({
  vertical,
  extendClasses,
  children,
  value: customValue,
}) => {
  const [value, setValue] = useState(customValue);
  const context: TabsContextResponse = useMemo(
    () => ({
      value,
      handleActive: (value) => setValue(value),
    }),
    [value]
  );
  useEffect(() => {
    setValue(customValue);
  }, [customValue]);
  return (
    <TabsContext.Provider value={context}>
      <div
        className={classNames({
          tfcTabs: true,
          [extendClasses as string]: !!extendClasses,
          [`tfcTabs-vertical`]: vertical,
        })}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
};

interface TabsListProps {
  /**
   * Extend classname
   */
  extendClasses?: string;
  /**
   * Content
   */
  children?: React.ReactNode;
}

const List: React.FC<TabsListProps> = ({ extendClasses, children }) => (
  <div
    className={classNames({
      ["tfcTabs-list"]: true,
      [extendClasses as string]: !!extendClasses,
    })}
  >
    {children}
  </div>
);

interface TabsItemProps {
  /**
   * Extend classname
   */
  extendClasses?: string;
  /**
   * Class active
   */
  activeClass?: string;
  /**
   * Value
   */
  value: string;
  /**
   * Content
   */
  children?: React.ReactNode;
  /**
   * Handle change tab
   */
  handleChangeTab?: (value: string) => void;
}

const Item: React.FC<TabsItemProps> = ({
  extendClasses,
  value,
  activeClass,
  children,
  handleChangeTab,
}) => {
  const { value: contextValue, handleActive } = useContext(TabsContext);
  return (
    <button
      className={classNames({
        ["tfcTabs-item"]: true,
        ["tfcTabs-item-active"]: !activeClass && value === contextValue,
        [activeClass as string]: !!activeClass && value === contextValue,
        [extendClasses as string]: !!extendClasses,
      })}
      onClick={() => {
        if (handleChangeTab) {
          handleChangeTab(value);
        }
        if (handleActive) {
          handleActive(value);
        }
      }}
    >
      {children}
    </button>
  );
};

interface TabsContentProps {
  /**
   * Extend classname
   */
  extendClasses?: string;
  /**
   * Value
   */
  value: string;
  /**
   * Content
   */
  children?: React.ReactNode;
}

const Content: React.FC<TabsContentProps> = ({
  extendClasses,
  value,
  children,
}) => {
  const { value: contextValue } = useContext(TabsContext);

  if (value !== contextValue) {
    return null;
  }

  return (
    <div
      className={classNames({
        ["tfcTabs-content"]: true,
        [extendClasses as string]: !!extendClasses,
      })}
    >
      {children}
    </div>
  );
};

export const Tabs = Object.assign(Root, { List, Item, Content });
